import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMarkets } from '../../hooks/useMarkets.js'
import { useSelectedMarkets } from '../../hooks/useSelectedMarkets.js'

export function useMarketSelectorLogic() {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { availableMarkets, getMarketByUid, isLoading } = useMarkets()
  const { selectedMarkets } = useSelectedMarkets()

  const [localSelectedMarkets, setLocalSelectedMarkets] = useState([])
  const [isDirty, setIsDirty] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const sortedMarkets = useMemo(
    () =>
      availableMarkets.reduce((regions, m) => {
        const region = m.region || 'Other'
        if (!regions[region]) regions[region] = []
        regions[region].push({
          uid: m.uid,
          name: m.name + (m.hidden ? ' (hidden)' : ''),
          state: m.state || 'Other',
          region,
        })
        return regions
      }, {}),
    [availableMarkets],
  )

  // Keyed on the URL selection alone. Including availableMarkets would discard
  // an in-progress selection every time the markets query refetched.
  useEffect(() => {
    setLocalSelectedMarkets(selectedMarkets)
  }, [selectedMarkets])

  const selectAll = useCallback(() => {
    setIsDirty(true)
    setLocalSelectedMarkets([])
  }, [])

  const selectMarket = useCallback((marketUid) => {
    setLocalSelectedMarkets((prev) => [...prev, marketUid])
    setIsDirty(true)
  }, [])

  const deselectMarket = useCallback((marketUid) => {
    setLocalSelectedMarkets((prev) => prev.filter((m) => m !== marketUid))
    setIsDirty(true)
  }, [])

  const toggleMarket = useCallback(
    (regionKey) => {
      let newSelection = [...localSelectedMarkets]
      let addedSelection = false

      sortedMarkets[regionKey]?.forEach((m) => {
        const selected = newSelection.includes(m.uid)
        if (!selected) {
          newSelection.push(m.uid)
          addedSelection = true
        }
      })

      if (!addedSelection) {
        sortedMarkets[regionKey]?.forEach((mkt) => {
          newSelection = newSelection.filter((m) => m !== mkt.uid)
        })
      }

      setLocalSelectedMarkets(newSelection)
      setIsDirty(true)
    },
    [localSelectedMarkets, sortedMarkets],
  )

  const applySelectedMarkets = useCallback(() => {
    setIsDirty(false)

    const filter = params.filter || 'open'
    const csvMarkets =
      localSelectedMarkets.length > 0 ? localSelectedMarkets.join(',') : 'all'

    if (location.pathname.startsWith('/containers')) {
      const next = new URLSearchParams(location.search)
      next.set('market', csvMarkets)
      navigate(`${location.pathname}?${next.toString()}`)
    } else {
      navigate(`/marketplace/${csvMarkets}/${filter}${location.search}`)
    }

    setTimeout(() => {
      setIsOpen(false)
    }, 100)
  }, [localSelectedMarkets, params, navigate, location])

  const displayLabel = useMemo(() => {
    const firstMarket =
      localSelectedMarkets.length > 0
        ? getMarketByUid(localSelectedMarkets[0])
        : null
    return {
      name: firstMarket?.name || 'All Markets',
      count: localSelectedMarkets.length || 0,
    }
  }, [localSelectedMarkets, getMarketByUid])

  const marketsDisplay = useMemo(() => {
    const items = []
    Object.keys(sortedMarkets)
      .sort()
      .forEach((key) => {
        if (key !== 'Other') {
          items.push({ uid: key, name: key.toUpperCase(), header: true })
          sortedMarkets[key]?.forEach((m) => items.push(m))
        }
      })

    if (sortedMarkets['Other']?.length) {
      items.push({ uid: 'Other', name: 'OTHER', header: true })
      sortedMarkets['Other']?.forEach((m) => items.push(m))
    }

    return items
  }, [sortedMarkets])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setIsDirty(false)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    if (isDirty) {
      setLocalSelectedMarkets(selectedMarkets)
      setIsDirty(false)
    }
  }, [isDirty, selectedMarkets])

  return {
    isLoading,
    isOpen,
    isDirty,
    localSelectedMarkets,
    displayLabel,
    marketsDisplay,
    handleOpen,
    handleClose,
    selectAll,
    selectMarket,
    deselectMarket,
    toggleMarket,
    applySelectedMarkets,
  }
}
